import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { ICampaignGroup } from '~/modules/campaign-group/types';
import type { CreateVariantInput } from '~/types/graphql';
import type { AssetTemplate } from '~/modules/shopify';
import type { IThemePage } from '~/stores/themePage';

import {
  parseTemplateInfoBy,
  parseTemplateIdBy,
  getLabelByTemplateType,
  capitalizeFirstLetter,
  getTemplateSuffixBySuffix,
} from '~/utils';
import { ProviderSource, TemplateType } from '~/types/template';
import { findVariantByOrigin } from '~/modules/campaign/utils';

export const getExistingCampaigns = (
  templateSuffix: string,
  campaignGroupCampaigns: ICampaignTemplate[],
): ICampaignTemplate[] => {
  return (
    campaignGroupCampaigns?.filter((campaign) => {
      const variantA = findVariantByOrigin(campaign, true);
      return variantA?.templateSuffix === templateSuffix;
    }) || []
  );
};

export const getNameByTemplateSuffix = (templateSuffix?: string): string => {
  const templateInfo = parseTemplateInfoBy({ templateSuffix });
  const templateName = capitalizeFirstLetter(getLabelByTemplateType(templateInfo?.type) || '');

  return `${templateName} testing`;
};

export const getArrayNameByTemplateSuffix = (): string[] => {
  const listPageType = [
    TemplateType.COLLECTION,
    TemplateType.PRODUCT,
    TemplateType.PAGE,
    TemplateType.ARTICLE,
    TemplateType.HOME,
  ];
  const listName = listPageType.map((type) =>
    getNameByTemplateSuffix(getTemplateSuffixBySuffix(type, 'test')),
  );

  return listName;
};

export const getUsedPageIds = (existingCampaigns: ICampaignTemplate[]): string[] => {
  return existingCampaigns.flatMap((campaign) => campaign.conditions?.targetPageIds ?? []);
};

export const getAvailablePageIds = (
  usedPageIds: string[],
  templateSuffix: string,
  assets: AssetTemplate[],
): string[] => {
  const assignedPageIds =
    assets.find((asset) => asset.key === templateSuffix)?.assigned?.data.map((item) => item.id) ||
    [];
  return assignedPageIds.filter((id) => !usedPageIds.includes(id));
};

export const getCampaignTargetPageIds = (
  templateSuffix: string,
  campaignGroupCampaigns: ICampaignTemplate[],
  assetsWithAssigned: AssetTemplate[],
): string[] => {
  const existingCampaigns = getExistingCampaigns(templateSuffix, campaignGroupCampaigns);
  const usedPageIds = getUsedPageIds(existingCampaigns);
  const availablePageIds = getAvailablePageIds(usedPageIds, templateSuffix, assetsWithAssigned);

  return existingCampaigns.length ? availablePageIds : [];
};

export const getTrafficRate = (campaignGroup?: ICampaignGroup) => {
  const trafficRate = campaignGroup?.trafficRate ?? 0.5;
  const trafficRateA = trafficRate;
  const trafficRateB = 1 - trafficRate;

  return {
    trafficRateA,
    trafficRateB,
  };
};

export const getThemePageIdByVariant = (
  variant?: Partial<CreateVariantInput>,
  themePages?: IThemePage[],
) => {
  let themePageID = '0';
  const templateInfo = parseTemplateInfoBy({
    templateSuffix: variant?.templateSuffix,
    themePages,
  });
  if (templateInfo && templateInfo.providerSource === ProviderSource.GEMPAGESV7) {
    themePageID = parseTemplateIdBy(templateInfo.suffix, ProviderSource.GEMPAGESV7) ?? '0';
  }

  return themePageID;
};
